import { Chip, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { pushDrill } from 'app/slice/executive.slice'
import LeaderboardCard from './LeaderboardCard'
import type { LeaderboardRow } from './LeaderboardCard'
import { useTopDoctors } from '../hooks/use-top-doctors'
import { formatCurrency } from 'utils/executive.utils'

// Key-person dependency thresholds — same shape as procedures, with an extra
// rows.length guard so a single-doctor clinic doesn't trigger a trivial "100%
// concentrated" chip the operator already knows.
const TOP1_CONCENTRATION_PCT = 40
const TOP3_CONCENTRATION_PCT = 70
const MIN_ROWS_FOR_CONCENTRATION = 3

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function TopDoctorsCard() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { data, loading, error, refetch } = useTopDoctors()

  const title = t('EXECUTIVE.SALES.TOP_DOCTORS.TITLE')

  const rows: LeaderboardRow[] =
    data?.items.map((i) => ({
      id: i.doctorId,
      name: i.doctorName,
      revenue: i.revenue,
      count: i.caseCount,
      avgPerItem: i.avgPerCase,
      percentShare: i.percentShare,
      variance: varianceOf(i.revenue, i.previousRevenue),
    })) ?? []

  const top1Share = rows[0]?.percentShare ?? 0
  const top3Share = rows.slice(0, 3).reduce((a, r) => a + (r.percentShare || 0), 0)
  const concentrated =
    rows.length >= MIN_ROWS_FOR_CONCENTRATION &&
    (top1Share >= TOP1_CONCENTRATION_PCT || top3Share >= TOP3_CONCENTRATION_PCT)

  const subtitle = data
    ? t(
        'EXECUTIVE.SALES.TOP_DOCTORS.SUBTITLE',
        'Top {{count}} หมอ • รวม {{total}}',
        {
          count: rows.length,
          total: formatCurrency(data.totalRevenue),
        }
      )
    : null

  const titleTooltip = t(
    'EXECUTIVE.SALES.TOP_DOCTORS.TOOLTIP',
    '10 หมอที่ทำรายได้สูงสุดในช่วงวันที่เลือก — เรียงตามยอดที่ลูกค้าชำระจริง (ไม่นับใบที่ถูกยกเลิก)\nหมายเหตุ: "สัดส่วน" = % ของ Top 10 / ชื่อรูปแบบ #ID คือหมอที่ user record ไม่พบในระบบ'
  )

  const notice = concentrated ? (
    <Chip
      icon={<WarningAmberOutlinedIcon />}
      label={
        <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
          {top1Share >= TOP1_CONCENTRATION_PCT
            ? t(
                'EXECUTIVE.SALES.TOP_DOCTORS.CONCENTRATION_TOP1',
                'พึ่งพาหมอคนเดียว: หมออันดับ 1 = {{pct}}% ของ Top 10',
                { pct: top1Share.toFixed(0) }
              )
            : t(
                'EXECUTIVE.SALES.TOP_DOCTORS.CONCENTRATION_TOP3',
                'รายได้กระจุก: Top 3 หมอ = {{pct}}% ของ Top 10',
                { pct: top3Share.toFixed(0) }
              )}
        </Typography>
      }
      color="warning"
      variant="outlined"
      size="small"
      sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
    />
  ) : null

  return (
    <LeaderboardCard
      title={title}
      titleTooltip={titleTooltip}
      subtitle={subtitle}
      notice={notice}
      emptyMessage={t(
        'EXECUTIVE.SALES.TOP_DOCTORS.EMPTY',
        'ไม่พบหมอที่ทำหัตถการในช่วงเวลาและสาขาที่เลือก'
      )}
      onClick={() => dispatch(pushDrill({ id: 'top-doctors', label: title }))}
      countColumnLabel={t('EXECUTIVE.SALES.COLUMN.CASES')}
      avgColumnLabel={t('EXECUTIVE.SALES.COLUMN.AVG_PER_CASE')}
      countUnit={t('EXECUTIVE.SALES.UNIT.CASE')}
      rows={rows}
      totalRevenue={data?.totalRevenue ?? 0}
      loading={loading}
      error={error}
      onRetry={refetch}
      columnHeaders={{
        rank: t('EXECUTIVE.SALES.COLUMN.RANK'),
        name: t('EXECUTIVE.SALES.COLUMN.NAME'),
        revenue: t('EXECUTIVE.SALES.COLUMN.REVENUE'),
        share: t('EXECUTIVE.SALES.COLUMN.SHARE'),
      }}
    />
  )
}
